/**
 * useReminderPermissionPrompt — manages permission request flow for reminders.
 *
 * Simple trigger logic:
 *   1. useLiveQuery checks if any reminder exists in db.reminder (isDeleted=0)
 *   2. If yes → check device permissions via NativeBridge
 *   3. If permissions are missing → show prompt dialog
 *   4. If all granted → do nothing (no listeners needed)
 *
 * This naturally triggers after initial sync when reminder data arrives via
 * Dexie Cloud. No localStorage flags needed — liveQuery is the sole trigger.
 *
 * Uses db.setting_local for snooze/opt-out persistence (not synced).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { Capacitor } from "@capacitor/core"
import { useLiveQueryImmediate } from "@/db/hooks"

import { db } from "@/db/schema"
import { useSettingLocal } from "@/features/settings/hooks/useSettingLocal"
import {
  type CapabilitySnapshot,
} from "@/shared/utils/device/devicePermissions"
import { NativeBridge } from "@/shared/plugins/NativeBridge"
import { useAuthStore } from "@/features/auth/stores/authStore"
import { addBreadcrumb } from "@/shared/telemetry/breadcrumbs"
import { trackTelemetryEvent } from "@/shared/telemetry/client"
import { LogCategory } from "@/shared/telemetry/types"

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const SETTING_KEY = "#reminder-permission-prompt"
const SNOOZE_DURATION_MS = 12 * 60 * 60 * 1000 // 12 hours
const POST_DENY_GRACE_MS = 8_000 // 8 seconds after OS deny

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type PermissionKey = "push" | "alarm"

export interface PermissionEntry {
  key: PermissionKey
  status: "granted" | "denied" | "unknown"
  label: string
}

interface PromptSettings {
  version: 1
  lastPromptAt?: number
  snoozedUntil?: number
  optedOut?: boolean
  firstClickedAt?: Partial<Record<PermissionKey, number>>
}

export interface ReminderPermissionPromptState {
  open: boolean
  entries: PermissionEntry[]
  missingPermissions: PermissionKey[]
  isRequesting: boolean
  handleEnable: () => Promise<void>
  handleLater: () => void
  handleOptOut: () => void
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function defaultSettings(): PromptSettings {
  return { version: 1 }
}

function parseSettings(raw: string | undefined): PromptSettings {
  if (!raw) return defaultSettings()
  try {
    return JSON.parse(raw) as PromptSettings
  } catch {
    return defaultSettings()
  }
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useReminderPermissionPrompt(): ReminderPermissionPromptState {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [isRequesting, setIsRequesting] = useState(false)
  const [capabilities, setCapabilities] = useState<CapabilitySnapshot | null>(null)
  const denyGraceRef = useRef(false)
  const hasPromptedThisSessionRef = useRef(false)

  // ── Subscribe reactively to initialSyncActive ──
  const initialSyncActive = useAuthStore((s) => s.initialSyncActive)

  // ── Persisted prompt state (setting_local, not synced) ──
  const { value: settingsRaw, setValue: setSettingsRaw } = useSettingLocal<string>(
    SETTING_KEY,
    "",
  )
  const settings = useMemo(() => parseSettings(settingsRaw), [settingsRaw])

  const saveSettings = useCallback(
    async (patch: Partial<PromptSettings>) => {
      const next = { ...settings, ...patch, version: 1 as const }
      await setSettingsRaw(JSON.stringify(next))
    },
    [settings, setSettingsRaw],
  )

  // ── Core trigger: does the user have ANY reminder in their data? ──
  // Uses simple count() — most reliable liveQuery observable.
  // Any record in the reminder table (even deleted) means user has used reminders.
  const [reminderCount] = useLiveQueryImmediate(
    () => db.reminder.count(),
    ["reminderCount"],
    0,
  )
  const hasReminders = reminderCount > 0

  // ── Check capabilities when hasReminders becomes true ──
  // NOTE: Uses NativeBridge directly (not getDeviceCapabilitySnapshot) because
  // the dynamic import in devicePermissions.ts hangs in Capacitor WKWebView.
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return
    if (!hasReminders) return

    const check = async () => {
      try {
        const [pushResult, alarmResult] = await Promise.all([
          NativeBridge.getNotificationPermissionStatus(),
          NativeBridge.getAlarmPermissionStatus(),
        ])
        const snap: CapabilitySnapshot = {
          pushPermission: pushResult.status as "granted" | "denied" | "unknown",
          alarmPermission: alarmResult.status as "granted" | "denied" | "unknown",
          localNotificationPermission: pushResult.status as "granted" | "denied" | "unknown",
        }
        setCapabilities(snap)
      } catch (e) {
        console.warn("[Permissions] capability check failed:", e)
      }
    }
    void check()
  }, [hasReminders])

  // ── Also refresh capabilities on app foreground ──
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return

    let handle: { remove: () => void } | null = null
    const setup = async () => {
      if (!Capacitor.isPluginAvailable("App")) return
      const { App } = await import("@capacitor/app")
      handle = await App.addListener("appStateChange", ({ isActive }) => {
        if (isActive) {
          void (async () => {
            try {
              const [pushResult, alarmResult] = await Promise.all([
                NativeBridge.getNotificationPermissionStatus(),
                NativeBridge.getAlarmPermissionStatus(),
              ])
              setCapabilities({
                pushPermission: pushResult.status as "granted" | "denied" | "unknown",
                alarmPermission: alarmResult.status as "granted" | "denied" | "unknown",
                localNotificationPermission: pushResult.status as "granted" | "denied" | "unknown",
              })
            } catch {
              // Non-fatal
            }
          })()
        }
      })
    }
    void setup()

    return () => {
      if (handle) handle.remove()
    }
  }, [])

  // ── Build permission entries ──
  const entries = useMemo<PermissionEntry[]>(() => {
    if (!capabilities) return []

    const list: PermissionEntry[] = [
      {
        key: "push" as PermissionKey,
        status: capabilities.pushPermission ?? "unknown",
        label: t("Push notifications"),
      },
    ]

    // Alarm is relevant on Android (exact alarms) and iOS 26+ (AlarmKit)
    if (
      Capacitor.getPlatform() === "android" ||
      (capabilities.alarmPermission && capabilities.alarmPermission !== "unknown")
    ) {
      list.push({
        key: "alarm" as PermissionKey,
        status: capabilities.alarmPermission ?? "unknown",
        label: t("Alarm permission"),
      })
    }

    return list
  }, [capabilities, t])

  const missingPermissions = useMemo(
    () => entries.filter((e) => e.status !== "granted").map((e) => e.key),
    [entries],
  )

  // ── Evaluate whether to show the prompt ──
  // Simple: hasReminders + missing permissions + not opted out/snoozed
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return
    if (!hasReminders) return
    if (!capabilities) return
    if (denyGraceRef.current) return
    if (initialSyncActive) return

    if (settings.optedOut) {
      console.log("[Permissions] prompt suppressed — opted out")
      return
    }

    const snoozedUntil = settings.snoozedUntil ?? 0
    if (snoozedUntil > Date.now()) {
      console.log("[Permissions] prompt snoozed until", new Date(snoozedUntil).toISOString())
      return
    }

    const shouldShow = missingPermissions.length > 0

    console.log("[Permissions] evaluate:", {
      hasReminders,
      missingPermissions: missingPermissions.join(","),
      shouldShow,
      open,
    })

    if (shouldShow && !open && !hasPromptedThisSessionRef.current) {
      hasPromptedThisSessionRef.current = true
      setOpen(true)
      void saveSettings({ lastPromptAt: Date.now() })
      addBreadcrumb({
        category: "lifecycle",
        message: "[Permissions] prompt shown",
        data: { missing: missingPermissions.join(",") },
      })
      trackTelemetryEvent({
        name: "reminder_permission_prompt_shown",
        category: LogCategory.USER_ACTION,
        properties: { missing: missingPermissions.join(",") },
      })
    }
  }, [capabilities, hasReminders, missingPermissions, open, settings, saveSettings, initialSyncActive])

  // ── Actions ──

  const handleEnable = useCallback(async () => {
    if (isRequesting) return
    setIsRequesting(true)

    try {
      for (const key of missingPermissions) {
        const firstClicked = settings.firstClickedAt ?? {}
        if (!firstClicked[key]) {
          await saveSettings({
            firstClickedAt: { ...firstClicked, [key]: Date.now() },
          })
        }

        if (key === "push") {
          const pushRes = await NativeBridge.requestNotificationPermission()
          const result = pushRes.status as "granted" | "denied" | "unknown"
          addBreadcrumb({ category: "lifecycle", message: `[Permissions] push result: ${result}` })
          trackTelemetryEvent({
            name: "reminder_permission_result",
            category: LogCategory.USER_ACTION,
            properties: { key: "push", result },
          })
          if (result === "denied") {
            denyGraceRef.current = true
            setTimeout(() => {
              denyGraceRef.current = false
            }, POST_DENY_GRACE_MS)
          }
        } else if (key === "alarm") {
          await NativeBridge.requestAlarmPermission()
          addBreadcrumb({ category: "lifecycle", message: "[Permissions] alarm requested" })
          trackTelemetryEvent({
            name: "reminder_permission_result",
            category: LogCategory.USER_ACTION,
            properties: { key: "alarm", result: "requested" },
          })
        }

        // Refresh capabilities after permission request
        const [pushRefresh, alarmRefresh] = await Promise.all([
          NativeBridge.getNotificationPermissionStatus(),
          NativeBridge.getAlarmPermissionStatus(),
        ])
        const snap: CapabilitySnapshot = {
          pushPermission: pushRefresh.status as "granted" | "denied" | "unknown",
          alarmPermission: alarmRefresh.status as "granted" | "denied" | "unknown",
          localNotificationPermission: pushRefresh.status as "granted" | "denied" | "unknown",
        }
        setCapabilities(snap)

        // Process one permission at a time
        break
      }

      // Check if all now granted
      const [pushLatest, alarmLatest] = await Promise.all([
        NativeBridge.getNotificationPermissionStatus(),
        NativeBridge.getAlarmPermissionStatus(),
      ])
      const latestSnap: CapabilitySnapshot = {
        pushPermission: pushLatest.status as "granted" | "denied" | "unknown",
        alarmPermission: alarmLatest.status as "granted" | "denied" | "unknown",
        localNotificationPermission: pushLatest.status as "granted" | "denied" | "unknown",
      }
      setCapabilities(latestSnap)
      const stillMissing = entries.filter(
        (e) =>
          (e.key === "push" ? latestSnap.pushPermission : latestSnap.alarmPermission) !==
          "granted",
      )

      if (stillMissing.length === 0) {
        addBreadcrumb({ category: "lifecycle", message: "[Permissions] all granted" })
        trackTelemetryEvent({
          name: "reminder_permission_all_granted",
          category: LogCategory.USER_ACTION,
        })
        try {
          await NativeBridge.runSyncNow({ reason: "permission_granted" })
        } catch {
          // Non-fatal
        }
        setOpen(false)
        await saveSettings({ snoozedUntil: undefined, lastPromptAt: Date.now() })
      }
    } finally {
      setIsRequesting(false)
    }
  }, [isRequesting, missingPermissions, settings, saveSettings, entries])

  const handleLater = useCallback(async () => {
    setOpen(false)
    addBreadcrumb({ category: "lifecycle", message: "[Permissions] user chose later" })
    trackTelemetryEvent({ name: "reminder_permission_later", category: LogCategory.USER_ACTION })
    await saveSettings({ snoozedUntil: Date.now() + SNOOZE_DURATION_MS })
  }, [saveSettings])

  const handleOptOut = useCallback(async () => {
    setOpen(false)
    addBreadcrumb({ category: "lifecycle", message: "[Permissions] user opted out" })
    trackTelemetryEvent({ name: "reminder_permission_opt_out", category: LogCategory.USER_ACTION })
    await saveSettings({ optedOut: true })
  }, [saveSettings])

  return {
    open,
    entries,
    missingPermissions,
    isRequesting,
    handleEnable,
    handleLater,
    handleOptOut,
  }
}
